import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { type SubmitEvent, useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@lawline/ui/components/dialog";
import { Input } from "@lawline/ui/components/input";
import { Label } from "@lawline/ui/components/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@lawline/ui/components/select";

import { authClient } from "@platform/auth/client";
import { invitationsQueryOptions } from "../tenant.queries";
import type { Member } from "../tenant.types";

const INVITE_ROLES: Member["role"][] = ["member", "admin"];

// The new invitation shows up as a pending row in the roster.
export function InviteMemberDialog() {
	const t = useTranslations("tenant.inviteMember");
	const queryClient = useQueryClient();
	const [open, setOpen] = useState(false);
	const [email, setEmail] = useState("");
	const [role, setRole] = useState<Member["role"]>("member");
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function onOpenChange(next: boolean) {
		setOpen(next);
		if (!next) {
			setEmail("");
			setError(null);
		}
	}

	async function invite(e: SubmitEvent) {
		e.preventDefault();
		const trimmed = email.trim();
		if (!trimmed) return;

		setPending(true);
		setError(null);

		const { error: inviteError } = await authClient.organization.inviteMember({
			email: trimmed,
			role,
		});
		setPending(false);
		if (inviteError) {
			setError(inviteError.message ?? t("error"));
			return;
		}
		await queryClient.invalidateQueries({
			queryKey: invitationsQueryOptions.queryKey,
		});
		onOpenChange(false);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogTrigger asChild>
				<Button size="sm">
					<Plus className="size-4" />
					{t("trigger")}
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("title")}</DialogTitle>
					<DialogDescription>{t("description")}</DialogDescription>
				</DialogHeader>

				<form onSubmit={invite} className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<Label htmlFor="invite-email">{t("emailLabel")}</Label>
						<Input
							id="invite-email"
							type="email"
							placeholder={t("emailPlaceholder")}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
						/>
					</div>

					<div className="flex flex-col gap-2">
						<Label htmlFor="invite-role">{t("roleLabel")}</Label>
						<Select value={role} onValueChange={(v) => setRole(v as Member["role"])}>
							<SelectTrigger id="invite-role" className="capitalize">
								<SelectValue />
							</SelectTrigger>
							<SelectContent position="popper">
								{INVITE_ROLES.map((r) => (
									<SelectItem key={r} value={r} className="capitalize">
										{r}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{error && <p className="text-destructive text-sm">{error}</p>}

					<DialogFooter>
						<Button type="submit" disabled={pending || !email.trim()}>
							{pending ? t("sending") : t("submit")}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
